import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import ButtonBase from '@material-ui/core/ButtonBase';

import Menu from 'components/common/Menu';
import IconTriangleBottom from 'components/icons/IconTriangleBottom';
import IconCalendarStart from 'components/icons/IconCalendarStart';
import IconButton from 'components/common/IconButton';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';
import hexToRgb from 'styles/hexToRgb';

import dateFormat from 'config/dateFormat';
import * as validate from 'utils/validate';

import DateRangeFieldsForm from './DateRangeFieldsForm';

const styles = theme => ({
  root: {
    position: 'relative',
    maxWidth: '100%',
    margin: 0,
  },
  label: {
    position: 'relative',
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
    fontSize: pxToRem(12),
    letterSpacing: pxToEm(0.5),
    fontWeight: '700',
    color: theme.palette.grey[600],
    transform: 'none',
  },
  buttonMenu: {
    display: 'block',
    padding: 0,
    width: '100%',
    position: 'relative',
    fontFamily: theme.typography.fontFamily,
    '&:focus $buttonMenuContent': {
      borderRadius: 4,
      borderColor: theme.palette.primary[500],
      backgroundColor: `rgba(${hexToRgb(theme.palette.primary[500])}, 0.1)`,
    },
  },
  buttonMenuContent: {
    width: 'auto',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    lineHeight: 'calc(1em + 14px)',
    height: 'calc(1em + 14px)',
    cursor: 'pointer',
    textOverflow: 'ellipsis',
    textAlign: 'left',
    border: `1px solid ${theme.palette.grey[600]}`,
    userSelect: 'none',
    borderRadius: 4,
    color: theme.palette.secondary[800],
    padding: '0 35px 0 15px',
  },
  buttonMenuContentSm: {
    fontSize: pxToRem(13),
    letterSpacing: pxToEm(0.25),
  },
  buttonMenuContentMd: {
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5),
  },
  buttonMenuContentLg: {
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5),
    paddingTop: 3,
    paddingBottom: 4,
  },
  buttonMenuContentXl: {
    fontSize: pxToRem(16),
    letterSpacing: pxToEm(0.5, 16),
    paddingTop: 3,
    paddingBottom: 3,
  },
  buttonMenuContentDark: {
    backgroundColor: theme.palette.grey[100],
  },
  buttonMenuContentLight: {
    backgroundColor: theme.palette.common.white,
  },
  buttonMenuContentActive: {
    borderColor: theme.palette.primary[500],
    backgroundColor: `rgba(${hexToRgb(theme.palette.primary[500])}, 0.1)`,
  },
  buttonMenuContentDisabled: {
    color: `rgba(${hexToRgb(theme.palette.secondary[800])}, 0.4)`,
  },
  buttonMenuContentError: {
    borderColor: theme.palette.validation[500],
  },
  buttonMenuAdorment: {
    position: 'absolute',
    top: '50%',
    right: 13,
    pointerEvents: 'none',
    transform: 'translateY(-50%)',
  },
  iconTriangleBottom: {
    display: 'block',
    width: 10,
    height: 5,
    margin: '5px 0',
    color: theme.palette.grey[600],
  },
  menuContent: {
    width: 254,
    paddingTop: 8,
    [theme.breakpoints.down('sm')]: {
      margin: '0 auto',
    },
  },
  iconCalendarStart: {
    width: 16,
    height: 18,
  },
  iconButton: {
    margin: -8,
  },
  iconButtonActive: {
    color: theme.palette.primary[500],
  },
});

class DateRangeFields extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
    };

    this.onLabelClick = this.onLabelClick.bind(this);
    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onMenuButtonKeyDown = this.onMenuButtonKeyDown.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onCancelButtonClick = this.onCancelButtonClick.bind(this);
    this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
  }

  onLabelClick() {
    this.buttonRef.focus();
  }

  onMenuButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      anchorElWidth: event.currentTarget.getBoundingClientRect().width,
    });
  }

  onMenuButtonKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setState({ anchorEl: event.currentTarget });
    }
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onCancelButtonClick() {
    this.setState({ anchorEl: null });
  }

  onSubmitButtonClick() {
    this.setState({ anchorEl: null });
  }

  render() {
    const {
      size,
      color,
      label,
      id,
      disabled,
      placeholder,
      fullWidth,
      placement,
      datepickerPlacement,
      classes,
      dateOfCreationLabel,
      variant,
      className,
      ...otherProps
    } = this.props;
    const { anchorEl } = this.state;
    const open = Boolean(anchorEl);
    const values = [
      ...otherProps['Date of Creation From']
        && otherProps['Date of Creation From'].input.value
        && !validate.date(otherProps['Date of Creation From'].input.value)
        ? [`Date of Creation From: ${otherProps['Date of Creation From'].input.value}`] : [],
      ...otherProps['Date of Creation To']
        && otherProps['Date of Creation To'].input.value
        && !validate.date(otherProps['Date of Creation To'].input.value)
        ? [`Date of Creation To: ${otherProps['Date of Creation To'].input.value}`] : [],
      ...otherProps['Date of Service From']
        && otherProps['Date of Service From'].input.value
        && !validate.date(otherProps['Date of Service From'].input.value)
        ? [`Date of Service From: ${otherProps['Date of Service From'].input.value}`] : [],
      ...otherProps['Date of Service To']
        && otherProps['Date of Service To'].input.value
        && !validate.date(otherProps['Date of Service To'].input.value)
        ? [`Date of Service To: ${otherProps['Date of Service To'].input.value}`] : [],
      ...otherProps['Date of Delivery From']
        && otherProps['Date of Delivery From'].input.value
        && !validate.date(otherProps['Date of Delivery From'].input.value)
        ? [`Date of Delivery From: ${otherProps['Date of Delivery From'].input.value}`] : [],
      ...otherProps['Date of Delivery To']
        && otherProps['Date of Delivery To'].input.value
        && !validate.date(otherProps['Date of Delivery To'].input.value)
        ? [`Date of Delivery To: ${otherProps['Date of Delivery To'].input.value}`] : [],
      ...otherProps['Date of Claim Submission From']
        && otherProps['Date of Claim Submission From'].input.value
        && !validate.date(otherProps['Date of Claim Submission From'].input.value)
        ? [`Date of Claim Submission From: ${otherProps['Date of Claim Submission From'].input.value}`] : [],
      ...otherProps['Date of Claim Submission To']
        && otherProps['Date of Claim Submission To'].input.value
        && !validate.date(otherProps['Date of Claim Submission To'].input.value)
        ? [`Date of Claim Submission To: ${otherProps['Date of Claim Submission To'].input.value}`] : [],
    ];
    return (
      <FormControl
        className={classNames(classes.root, className)}
        fullWidth={fullWidth}
      >
        {(label.length > 0 && variant === 'text') &&
          <InputLabel
            className={classNames(classes.label)}
            disableAnimation
            htmlFor={id}
            shrink={false}
            onClick={this.onLabelClick}
          >
            {label}
          </InputLabel>
        }
        {variant === 'icon' ? (
          <IconButton
            className={classNames(classes.iconButton, {
              [classes.iconButtonActive]: values.length > 0,
            })}
            onClick={this.onMenuButtonClick}
            onKeyDown={this.onMenuButtonKeyDown}
            disabled={disabled}
            id={id}
            aria-label="Selectmenu"
            aria-owns={open ? `${id}-date-range-menu` : null}
            aria-haspopup="true"
            buttonRef={(buttonRef) => { this.buttonRef = buttonRef; }}
            tooltipContent={values.length > 0
              ? values.join(', ')
              : label}
          >
            <IconCalendarStart
              className={classes.iconCalendarStart}
            />
          </IconButton>
        ) : (
          <ButtonBase
            className={classes.buttonMenu}
            onClick={this.onMenuButtonClick}
            // onFocus={input.onFocus}
            onKeyDown={this.onMenuButtonKeyDown}
            disabled={disabled}
            disableRipple
            id={id}
            aria-label="Selectmenu"
            aria-owns={open ? `${id}-date-range-menu` : null}
            aria-haspopup="true"
            buttonRef={(buttonRef) => { this.buttonRef = buttonRef; }}
          >
            <div
              className={classNames(classes.buttonMenuContent, {
                [classes.buttonMenuContentSm]: size === 'sm',
                [classes.buttonMenuContentMd]: size === 'md',
                [classes.buttonMenuContentLg]: size === 'lg',
                [classes.buttonMenuContentXl]: size === 'xl',
                [classes.buttonMenuContentLight]: color === 'light',
                [classes.buttonMenuContentDark]: color === 'dark',
                [classes.buttonMenuContentActive]: open,
                [classes.buttonMenuContentDisabled]: disabled,
              })}
            >
              {placeholder}
              <div className={classes.buttonMenuAdorment}>
                <IconTriangleBottom className={classes.iconTriangleBottom} />
              </div>
            </div>
          </ButtonBase>
        )}
        <Menu
          id={`${id}-date-range-menu`}
          anchorEl={anchorEl}
          open={open}
          onClose={this.onMenuClose}
          arrow={false}
          placement={placement}
          padding="none"
          list={false}
        >
          <div className={classes.menuContent}>
            <DateRangeFieldsForm
              form={`${id}-form`}
              initialValues={{
                ...otherProps['Date of Service From'] ? {
                  'Date of Service From': otherProps['Date of Service From'].input.value,
                } : {},
                ...otherProps['Date of Service To'] ? {
                  'Date of Service To': otherProps['Date of Service To'].input.value,
                } : {},
                ...otherProps['Date of Creation From'] ? {
                  'Date of Creation From': otherProps['Date of Creation From'].input.value,
                } : {},
                ...otherProps['Date of Creation To'] ? {
                  'Date of Creation To': otherProps['Date of Creation To'].input.value,
                } : {},
                ...otherProps['Date of Delivery From'] ? {
                  'Date of Delivery From': otherProps['Date of Delivery From'].input.value,
                } : {},
                ...otherProps['Date of Delivery To'] ? {
                  'Date of Delivery To': otherProps['Date of Delivery To'].input.value,
                } : {},
                ...otherProps['Date of Claim Submission From'] ? {
                  'Date of Claim Submission From': otherProps['Date of Claim Submission From'].input.value,
                } : {},
                ...otherProps['Date of Claim Submission To'] ? {
                  'Date of Claim Submission To': otherProps['Date of Claim Submission To'].input.value,
                } : {},
              }}
              onSubmitSuccess={(values) => {
                if (values['Date of Service From']
                  && values['Date of Service To']) {
                  otherProps['Date of Service From'].input
                    .onChange(moment(values['Date of Service From'], dateFormat).format(dateFormat));
                  otherProps['Date of Service To'].input
                    .onChange(moment(values['Date of Service To'], dateFormat).format(dateFormat));
                }

                if (values['Date of Creation From']
                  && values['Date of Creation To']) {
                  otherProps['Date of Creation From'].input
                    .onChange(moment(values['Date of Creation From'], dateFormat).format(dateFormat));
                  otherProps['Date of Creation To'].input
                    .onChange(moment(values['Date of Creation To'], dateFormat).format(dateFormat));
                }

                if (values['Date of Delivery From']
                  && values['Date of Delivery To']) {
                  otherProps['Date of Delivery From'].input
                    .onChange(moment(values['Date of Delivery From'], dateFormat).format(dateFormat));
                  otherProps['Date of Delivery To'].input
                    .onChange(moment(values['Date of Delivery To'], dateFormat).format(dateFormat));
                }
                if (values['Date of Claim Submission From']
                  && values['Date of Claim Submission To']) {
                  otherProps['Date of Claim Submission From'].input
                    .onChange(moment(values['Date of Claim Submission From'], dateFormat).format(dateFormat));
                  otherProps['Date of Claim Submission To'].input
                    .onChange(moment(values['Date of Claim Submission To'], dateFormat).format(dateFormat));
                }
                this.onMenuClose();
              }}
              onClear={() => {
                if (otherProps['Date of Service From']) {
                  otherProps['Date of Service From'].input.onChange('');
                }
                if (otherProps['Date of Service To']) {
                  otherProps['Date of Service To'].input.onChange('');
                }
                if (otherProps['Date of Creation From']) {
                  otherProps['Date of Creation From'].input.onChange('');
                }
                if (otherProps['Date of Creation To']) {
                  otherProps['Date of Creation To'].input.onChange('');
                }
                if (otherProps['Date of Delivery From']) {
                  otherProps['Date of Delivery From'].input.onChange('');
                }
                if (otherProps['Date of Delivery To']) {
                  otherProps['Date of Delivery To'].input.onChange('');
                }
                if (otherProps['Date of Claim Submission From']) {
                  otherProps['Date of Claim Submission From'].input.onChange('');
                }
                if (otherProps['Date of Claim Submission To']) {
                  otherProps['Date of Claim Submission To'].input.onChange('');
                }
                this.onMenuClose();
              }}
              dateOfCreation={Boolean(otherProps['Date of Creation From'] && otherProps['Date of Creation To'])}
              dateOfService={Boolean(otherProps['Date of Service From'] && otherProps['Date of Service To'])}
              dateOfDelivery={Boolean(otherProps['Date of Delivery From'] && otherProps['Date of Delivery To'])}
              dateOfClaimSubmission={Boolean(otherProps['Date of Claim Submission From'] && otherProps['Date of Claim Submission To'])}
              datepickerPlacement={datepickerPlacement}
              dateOfCreationLabel={dateOfCreationLabel}
            />
          </div>
        </Menu>
      </FormControl>
    );
  }
}

DateRangeFields.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  color: PropTypes.oneOf(['light', 'dark']),
  variant: PropTypes.oneOf(['icon', 'text']),
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  placeholder: PropTypes.string,
  placement: PropTypes.oneOf(['bottom-left', 'bottom-right']),
  datepickerPlacement: PropTypes.oneOf(['middle-left', 'middle-right']),
  dateOfCreationLabel: PropTypes.string,
  className: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    buttonMenu: PropTypes.string.isRequired,
    buttonMenuContent: PropTypes.string.isRequired,
    buttonMenuContentSm: PropTypes.string.isRequired,
    buttonMenuContentMd: PropTypes.string.isRequired,
    buttonMenuContentLg: PropTypes.string.isRequired,
    buttonMenuContentXl: PropTypes.string.isRequired,
    buttonMenuContentDark: PropTypes.string.isRequired,
    buttonMenuContentLight: PropTypes.string.isRequired,
    buttonMenuContentActive: PropTypes.string.isRequired,
    buttonMenuContentDisabled: PropTypes.string.isRequired,
    buttonMenuContentError: PropTypes.string.isRequired,
    buttonMenuAdorment: PropTypes.string.isRequired,
    iconTriangleBottom: PropTypes.string.isRequired,
    menuContent: PropTypes.string.isRequired,
  }).isRequired,
};

DateRangeFields.defaultProps = {
  label: '',
  size: 'lg',
  color: 'light',
  disabled: false,
  fullWidth: false,
  placeholder: '',
  placement: 'bottom-left',
  datepickerPlacement: 'middle-left',
  dateOfCreationLabel: null,
  variant: 'text',
  className: '',
};

export default withStyles(styles)(DateRangeFields);



// WEBPACK FOOTER //
// ./src/components/fields/DateRangeFields/DateRangeFields.js